import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'
import { MakePostRequest, headers } from '@/shared/utils'

const ALLOWED_TYPES = ['image/jpeg', 'image/heic', 'image/heif']

const POLL_INTERVAL_MS = 1500
// Generous backstop: the server gives up on unfinished images well before this
const POLL_TIMEOUT_MS = 5 * 60 * 1000

const containsFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files')

// HEIC files can arrive with an empty MIME type on some platforms, so fall back to the extension
const fileType = (file) => {
  if (file.type) return file.type
  if (/\.heic$/i.test(file.name)) return 'image/heic'
  if (/\.heif$/i.test(file.name)) return 'image/heif'
  return ''
}

// The visitor's own extension rather than the MIME type, which doesn't always
// agree with it — Chrome reports a .heic file as image/heif
const formatName = (file) => (file.name.match(/\.(\w+)$/)?.[1] || fileType(file).replace('image/', '')).toUpperCase()

// Connects to data-controller="image-description-dropzone"
// File picker + drag & drop for the image description tool page. Drag events are listened to at
// the document level so files can be dropped anywhere on the page; while dragging,
// the dropzone content swaps to a "release to describe" state. Selected images are
// uploaded straight to S3 via presigned POSTs, then the describe endpoint queues a
// job per image and we poll for the results section as a Turbo Stream.
export default class extends Controller {
  static targets = [
    'input',
    'title',
    'progress',
    'progressFill',
    'chooseButton',
    'dragTitle',
    'dragText',
    'notice',
    'challenge'
  ]
  static values = {
    maxImages: Number,
    maxFileSizeMb: Number,
    remaining: Number,
    turnstileSiteKey: String,
    presignUrl: String,
    describeUrl: String,
    resultsId: String
  }

  connect() {
    // The default title holds responsive spans, so keep the markup for restoring
    this.defaultTitleHtml = this.titleTarget.innerHTML
    this.previewUrls = []
    this.previewElements = []
    // dragenter/dragleave fire for every element the cursor passes over; the
    // drag is over the window while the counter is positive.
    this.dragDepth = 0
    this.documentDragHandlers = {
      dragenter: this.documentDragEnter.bind(this),
      dragover: this.documentDragOver.bind(this),
      dragleave: this.documentDragLeave.bind(this),
      drop: this.documentDrop.bind(this)
    }
    Object.entries(this.documentDragHandlers).forEach(([name, handler]) => document.addEventListener(name, handler))

    // A stream renders after renderStreamMessage returns, so the previews go
    // back into the rows by extending the render rather than following it
    this.streamRenderHandler = this.applyPreviewsAfterRender.bind(this)
    document.addEventListener('turbo:before-stream-render', this.streamRenderHandler)
  }

  disconnect() {
    Object.entries(this.documentDragHandlers).forEach(([name, handler]) => document.removeEventListener(name, handler))
    document.removeEventListener('turbo:before-stream-render', this.streamRenderHandler)
    this.releasePreviews()
  }

  openFilePicker() {
    this.inputTarget.click()
  }

  inputChanged() {
    this.selectFiles(Array.from(this.inputTarget.files))
    this.inputTarget.value = '' // allow re-selecting the same file(s)
  }

  documentDragEnter(event) {
    if (!containsFiles(event)) return
    event.preventDefault()
    if (this.uploading || this.limitReached()) return

    this.dragDepth += 1
    this.showDragState(this.draggedFileCount(event))
  }

  documentDragOver(event) {
    if (!containsFiles(event)) return
    // Required to allow dropping (and stops the browser opening the file)
    event.preventDefault()
    event.dataTransfer.dropEffect = this.uploading || this.limitReached() ? 'none' : 'copy'
  }

  documentDragLeave(event) {
    if (!containsFiles(event)) return

    this.dragDepth = Math.max(0, this.dragDepth - 1)
    if (this.dragDepth === 0) this.hideDragState()
  }

  documentDrop(event) {
    if (!containsFiles(event)) return
    event.preventDefault()

    this.dragDepth = 0
    this.hideDragState()
    this.selectFiles(Array.from(event.dataTransfer?.files || []))
  }

  draggedFileCount(event) {
    return Array.from(event.dataTransfer?.items || []).filter((item) => item.kind === 'file').length
  }

  showDragState(fileCount) {
    const imageWord = fileCount === 1 ? 'image' : 'images'
    this.dragTitleTarget.textContent = `Drop your ${imageWord}`
    this.dragTextTarget.textContent =
      fileCount > 0 ? `Release to upload ${fileCount} ${imageWord}` : 'Release to upload your images'
    this.element.classList.add('dragging')
  }

  hideDragState() {
    this.element.classList.remove('dragging')
  }

  selectFiles(files) {
    if (files.length === 0) return
    if (this.uploading || this.limitReached()) return
    this.hideNotice()

    const supported = files.filter((file) => ALLOWED_TYPES.includes(fileType(file)))
    const withinSizeLimit = supported.filter((file) => file.size <= this.maxFileSizeMbValue * 1024 * 1024)
    const images = withinSizeLimit.slice(0, Math.min(this.maxImagesValue, this.remainingValue))

    if (images.length === 0) {
      this.setTitle(
        supported.length === 0
          ? 'Only JPG or HEIC images are supported'
          : `Images must be ${this.maxFileSizeMbValue}MB or smaller`
      )
      return
    }

    this.showSkippedNotice(files, supported, withinSizeLimit, images)
    this.selectedFiles = images
    this.uploadFiles(images)
  }

  showSkippedNotice(files, supported, withinSizeLimit, images) {
    const unsupportedCount = files.length - supported.length
    const oversizedCount = supported.length - withinSizeLimit.length
    const overLimitCount = withinSizeLimit.length - images.length

    const messages = []
    if (unsupportedCount > 0) messages.push(`${this.fileCount(unsupportedCount)} skipped (not a supported format).`)
    if (oversizedCount > 0)
      messages.push(`${this.fileCount(oversizedCount)} skipped (larger than ${this.maxFileSizeMbValue}MB).`)
    if (overLimitCount > 0) messages.push(`Only the first ${images.length} images were uploaded.`)
    if (messages.length === 0) return

    this.noticeTarget.textContent = messages.join(' ')
    this.noticeTarget.hidden = false
  }

  hideNotice() {
    this.noticeTarget.hidden = true
  }

  fileCount(count) {
    return `${count} ${count === 1 ? 'file' : 'files'}`
  }

  async uploadFiles(images) {
    this.uploading = true
    this.uploadedKeys = []
    this.scrolledToResults = false
    this.createPreviews(images)
    this.setTitle(`Uploading ${this.imageCount(images)}…`)
    this.chooseButtonTarget.hidden = true
    this.element.classList.add('uploading')
    this.showProgress()

    try {
      const uploads = await this.requestPresignedUploads(images)
      const progressPerFile = images.map(() => 0)
      this.uploadedKeys = await Promise.all(
        uploads.map((upload, index) =>
          this.uploadToS3(images[index], upload, (fraction) => {
            progressPerFile[index] = fraction
            this.updateProgress(progressPerFile)
          })
        )
      )

      this.setTitle('Analysing images')
      this.startAnalysing()
      await this.pollForResults(await this.requestDescriptions())
      this.resetTitle()
      if (this.limitReached()) this.showLimitReached()
    } catch (error) {
      if (error.limitReached) this.showLimitReached()
      this.setTitle(error.userMessage || 'Something went wrong, please try again')
    } finally {
      this.stopAnalysing()
      this.hideProgress()
      this.chooseButtonTarget.hidden = false
      this.element.classList.remove('uploading')
      this.uploading = false
    }
  }

  async requestPresignedUploads(images, turnstileToken = null) {
    const payload = {
      files: images.map((file) => ({ name: file.name, type: fileType(file), size: file.size }))
    }
    if (turnstileToken) payload.turnstile_token = turnstileToken

    const response = await MakePostRequest(this.presignUrlValue, payload)
    if (response.ok) return (await response.json()).uploads

    const data = await response.json().catch(() => ({}))
    if (data.verification_required && !turnstileToken) {
      return this.requestPresignedUploads(images, await this.solveTurnstileChallenge())
    }

    const error = new Error('Presign request failed')
    error.userMessage = data.error
    error.limitReached = data.limit_reached
    throw error
  }

  // XMLHttpRequest rather than fetch so we get upload progress events
  uploadToS3(file, upload, onProgress) {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      Object.entries(upload.fields).forEach(([name, value]) => formData.append(name, value))
      formData.append('file', file)

      const request = new XMLHttpRequest()
      request.open('POST', upload.url)
      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) onProgress(event.loaded / event.total)
      })
      request.addEventListener('load', () => {
        if (request.status >= 200 && request.status < 300) {
          onProgress(1)
          resolve(upload.key)
        } else {
          reject(new Error(`S3 upload failed (${request.status})`))
        }
      })
      request.addEventListener('error', () => reject(new Error('S3 upload failed')))
      request.send(formData)
    })
  }

  // Queues the batch and returns the URL its results will appear at
  async requestDescriptions(turnstileToken = null) {
    const body = {
      files: this.uploadedKeys.map((key, index) => ({ key, name: this.selectedFiles[index].name }))
    }
    if (turnstileToken) body.turnstile_token = turnstileToken

    const response = await MakePostRequest(this.describeUrlValue, body)
    if (response.ok) return (await response.json()).status_url

    const data = await response.json().catch(() => ({}))
    if (data.verification_required && !turnstileToken) {
      return this.requestDescriptions(await this.solveTurnstileChallenge())
    }

    const error = new Error(`Describe request failed (${response.status})`)
    error.userMessage = data.error
    error.limitReached = data.limit_reached
    throw error
  }

  // A background job describes each image, so poll for the results section and
  // re-render it as descriptions land. The server answers 204 when nothing has
  // changed since the last poll, which leaves the rendered results untouched.
  async pollForResults(statusUrl) {
    const total = this.uploadedKeys.length
    const giveUpAt = Date.now() + POLL_TIMEOUT_MS
    let finished = null

    while (Date.now() < giveUpAt) {
      const requestHeaders = headers()
      requestHeaders.set('Accept', 'text/vnd.turbo-stream.html')
      const url = finished === null ? statusUrl : `${statusUrl}?finished=${finished}`
      const response = await fetch(url, { headers: requestHeaders, credentials: 'include' })

      if (!response.ok) throw this.resultsError(`Results request failed (${response.status})`)

      const remaining = response.headers.get('X-Image-Description-Remaining-Images')
      if (remaining !== null) this.remainingValue = parseInt(remaining, 10)

      if (response.status !== 204) {
        finished = parseInt(response.headers.get('X-Image-Description-Finished-Images'), 10)
        Turbo.renderStreamMessage(await response.text())
        if (!this.scrolledToResults) this.scrollToResults()
        this.scrolledToResults = true
        if (finished > 0 && finished < total) this.setTitle(`Analysing images (${finished} of ${total})`)
      }

      if (response.headers.get('X-Image-Description-Batch-Complete') === 'true') return

      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
    }

    throw this.resultsError('Timed out waiting for descriptions')
  }

  // The browser already has the images, so the results list shows them rather
  // than waiting on the server for something it can already draw. The same
  // elements are moved back into each row after every render, so nothing is
  // decoded twice.
  createPreviews(images) {
    this.releasePreviews()
    this.previewUrls = images.map((file) => URL.createObjectURL(file))
    this.previewElements = images.map((file, index) => this.buildPreview(file, index))
  }

  buildPreview(file, index) {
    const preview = new Image()
    preview.src = this.previewUrls[index]
    preview.alt = file.name
    preview.decoding = 'async'
    // Chrome and Firefox can't paint HEIC, so those rows name the format instead
    preview.addEventListener('error', () => this.replacePreview(index, this.buildFormatLabel(file)))
    return preview
  }

  buildFormatLabel(file) {
    const label = document.createElement('span')
    label.className = 'image-description-tool-results-thumb-format'
    label.textContent = formatName(file)
    return label
  }

  replacePreview(index, element) {
    this.previewElements[index].replaceWith(element)
    this.previewElements[index] = element
  }

  applyPreviewsAfterRender(event) {
    const render = event.detail.render
    event.detail.render = async (streamElement) => {
      await render(streamElement)
      this.applyPreviews()
    }
  }

  applyPreviews() {
    this.previewElements.forEach((element, index) => {
      document.querySelector(`[data-image-description-thumbnail="${index}"]`)?.replaceChildren(element)
    })
  }

  releasePreviews() {
    this.previewUrls.forEach((url) => URL.revokeObjectURL(url))
    this.previewUrls = []
    this.previewElements = []
  }

  resultsError(message) {
    const error = new Error(message)
    error.userMessage = 'This is taking longer than expected, please try again'
    return error
  }

  limitReached() {
    return this.remainingValue < 1
  }

  showLimitReached() {
    this.element.classList.add('limit-reached')
  }

  // Shows the Cloudflare Turnstile widget in place of the upload UI and resolves
  // with the token once the visitor passes the challenge
  async solveTurnstileChallenge() {
    this.element.classList.add('verifying')
    let widgetId
    try {
      await this.loadTurnstileScript()
      return await new Promise((resolve, reject) => {
        widgetId = window.turnstile.render(this.challengeTarget, {
          sitekey: this.turnstileSiteKeyValue,
          callback: resolve,
          'error-callback': () => reject(new Error('Turnstile challenge failed'))
        })
      })
    } finally {
      if (widgetId !== undefined) window.turnstile.remove(widgetId)
      this.element.classList.remove('verifying')
    }
  }

  loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve()

    this.turnstileScriptPromise ||= new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.onload = resolve
      script.onerror = () => reject(new Error('Turnstile script failed to load'))
      document.head.appendChild(script)
    })
    return this.turnstileScriptPromise
  }

  scrollToResults() {
    // Give the Turbo Stream a moment to render before scrolling to it
    setTimeout(() => {
      document.getElementById(this.resultsIdValue)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 150)
  }

  showProgress() {
    this.progressFillTarget.style.width = '0%'
    this.progressTarget.hidden = false
  }

  startAnalysing() {
    this.progressFillTarget.style.width = '100%'
    this.progressTarget.classList.add('analysing')
  }

  stopAnalysing() {
    this.progressTarget.classList.remove('analysing')
  }

  hideProgress() {
    this.progressTarget.hidden = true
  }

  updateProgress(progressPerFile) {
    const overallFraction = progressPerFile.reduce((sum, fraction) => sum + fraction, 0) / progressPerFile.length
    this.progressFillTarget.style.width = `${Math.round(overallFraction * 100)}%`
  }

  imageCount(images) {
    return `${images.length} ${images.length === 1 ? 'image' : 'images'}`
  }

  setTitle(text) {
    this.titleTarget.textContent = text
  }

  resetTitle() {
    this.titleTarget.innerHTML = this.defaultTitleHtml
  }
}
